import { useState, type ReactNode } from "react";
import { ChevronDown, Loader2, Search as SearchIcon, SlidersHorizontal, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import type {
  TikTokCreatorSearchCatalog,
  TikTokCreatorSearchFilters,
} from "@/services/tiktokShop.service";
import { formatCompactCount } from "@/utils/formatters";

import {
  AGE_OPTIONS,
  filterChips,
  FOLLOWER_PRESETS,
  GMV_OPTIONS,
  UNITS_OPTIONS,
} from "./creatorSearchFormat";

interface Props {
  catalog: TikTokCreatorSearchCatalog;
  filters: TikTokCreatorSearchFilters;
  onChange: (filters: TikTokCreatorSearchFilters) => void;
  onSearch: () => void;
  searching: boolean;
  /** Set while rate limited; the Search button stays disabled. */
  blockedReason?: string | null;
  disabled?: boolean;
}

const ANY = "__any";

/**
 * The creator-search form: four filters in the bar, the rest in a drawer,
 * and the active filters as removable chips. Changing a filter never runs a
 * search; only the Search button does.
 */
export function CreatorSearchFilters({ catalog, filters, onChange, onSearch, searching, blockedReason, disabled }: Props) {
  const [moreOpen, setMoreOpen] = useState(false);
  const set = (patch: Partial<TikTokCreatorSearchFilters>) => onChange({ ...filters, ...patch });
  const chips = filterChips(filters, catalog);
  const searchDisabled = disabled || searching || Boolean(blockedReason);
  // Category Pro is the niche control; without it the product tree stands in.
  const nicheFromCategories = catalog.niches.length === 0 && catalog.categories.length > 0;
  const moreCount = countMore(filters, nicheFromCategories);

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!searchDisabled) onSearch();
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="creator-search-keyword"
            value={filters.keyword ?? ""}
            onChange={(e) => set({ keyword: e.target.value })}
            placeholder="Username or name"
            className="pl-9"
            disabled={disabled}
            maxLength={100}
          />
        </div>

        {nicheFromCategories ? (
          <MultiSelect
            label="Niche"
            options={catalog.categories.map((c) => ({ value: c.id, label: c.name }))}
            selected={filters.categoryIds ?? []}
            onChange={(categoryIds) => set({ categoryIds })}
            disabled={disabled}
          />
        ) : catalog.niches.length > 0 ? (
          <MultiSelect
            label="Niche"
            options={catalog.niches.map((n) => ({ value: n.id, label: n.name }))}
            selected={filters.nicheIds ?? []}
            onChange={(nicheIds) => set({ nicheIds })}
            disabled={disabled}
          />
        ) : null}

        <MultiSelect
          label="GMV (30d)"
          options={GMV_OPTIONS}
          selected={filters.gmvRanges ?? []}
          onChange={(gmvRanges) => set({ gmvRanges: gmvRanges as TikTokCreatorSearchFilters["gmvRanges"] })}
          disabled={disabled}
        />

        <FollowersPicker
          min={filters.followersMin}
          max={filters.followersMax}
          onChange={(followersMin, followersMax) => set({ followersMin, followersMax })}
          disabled={disabled}
        />

        <Button type="button" variant="outline" onClick={() => setMoreOpen(true)} disabled={disabled}>
          <SlidersHorizontal className="h-4 w-4" />
          More filters
          {moreCount > 0 && (
            <span className="ml-0.5 rounded-full bg-brand-100 px-1.5 text-xs font-semibold text-brand-700 dark:text-brand-300">
              {moreCount}
            </span>
          )}
        </Button>

        <Button type="submit" disabled={searchDisabled} title={blockedReason ?? undefined}>
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <SearchIcon className="h-4 w-4" />}
          Search
        </Button>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-card py-0.5 pl-2.5 pr-1 text-xs text-foreground"
            >
              {chip.label}
              <button
                type="button"
                onClick={() => onChange(chip.remove(filters))}
                className="rounded-full p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label={`Remove ${chip.label}`}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => onChange(filters.sortBy ? { sortBy: filters.sortBy } : {})}
            className="px-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
            disabled={disabled}
          >
            Clear all
          </button>
        </div>
      )}

      <MoreFiltersSheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        catalog={catalog}
        filters={filters}
        onChange={onChange}
        showCategories={!nicheFromCategories && catalog.categories.length > 0}
        onApply={() => {
          setMoreOpen(false);
          if (!searchDisabled) onSearch();
        }}
      />
    </form>
  );
}

function countMore(f: TikTokCreatorSearchFilters, nicheFromCategories: boolean): number {
  return [
    f.ageRanges?.length,
    f.gender,
    f.languageIds?.length,
    f.minAvgVideoViews !== undefined,
    f.minEngagementRate !== undefined,
    f.minLiveViewers !== undefined,
    f.unitsSoldRanges?.length,
    f.maxCommissionRate,
    f.postRate,
    f.agencyStatus,
    f.fastGrowing,
    f.notInvitedLast90Days,
    f.creatorLevelIds?.length,
    !nicheFromCategories && f.categoryIds?.length,
  ].filter(Boolean).length;
}

function MultiSelect<T extends string>({
  label,
  options,
  selected,
  onChange,
  disabled,
}: {
  label: string;
  options: Array<{ value: T; label: string }>;
  selected: T[];
  onChange: (next: T[]) => void;
  disabled?: boolean;
}) {
  const summary =
    selected.length === 0
      ? label
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.label ?? label
        : `${label} · ${selected.length}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button type="button" variant="outline" className={cn("max-w-[14rem]", selected.length > 0 && "border-brand-300 text-brand-700 dark:text-brand-300")}>
          <span className="truncate">{summary}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 w-60 overflow-y-auto">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={selected.includes(option.value)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) =>
              onChange(checked ? [...selected, option.value] : selected.filter((v) => v !== option.value))
            }
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FollowersPicker({
  min,
  max,
  onChange,
  disabled,
}: {
  min?: number;
  max?: number;
  onChange: (min?: number, max?: number) => void;
  disabled?: boolean;
}) {
  const label =
    min !== undefined && max !== undefined
      ? `${formatCompactCount(min)}–${formatCompactCount(max)}`
      : min !== undefined
        ? `${formatCompactCount(min)}+`
        : max !== undefined
          ? `Up to ${formatCompactCount(max)}`
          : null;
  const parse = (value: string) => {
    const n = Number(value.replace(/[^\d]/g, ""));
    return value.trim() === "" || !Number.isFinite(n) ? undefined : n;
  };
  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        <Button type="button" variant="outline" className={cn(label && "border-brand-300 text-brand-700 dark:text-brand-300")}>
          {label ? `${label} followers` : "Followers"}
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor="creator-followers-min" className="text-xs">Minimum</Label>
            <Input
              id="creator-followers-min"
              inputMode="numeric"
              value={min ?? ""}
              onChange={(e) => onChange(parse(e.target.value), max)}
              placeholder="Any"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="creator-followers-max" className="text-xs">Maximum</Label>
            <Input
              id="creator-followers-max"
              inputMode="numeric"
              value={max ?? ""}
              onChange={(e) => onChange(min, parse(e.target.value))}
              placeholder="Any"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FOLLOWER_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => onChange(preset, max !== undefined && max < preset ? undefined : max)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs",
                min === preset ? "border-brand-400 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300" : "border-border hover:border-brand-300",
              )}
            >
              {formatCompactCount(preset)}+
            </button>
          ))}
        </div>
        {min !== undefined && max !== undefined && min > max && (
          <p className="text-xs text-destructive">Minimum must be less than the maximum.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}

function ChoiceRow<T extends string | number>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string }>;
  value: T | undefined;
  onChange: (value: T | undefined) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => {
        const active = value === option.value;
        return (
          <button
            key={String(option.value)}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(active ? undefined : option.value)}
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs",
              active ? "border-brand-400 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300" : "border-border hover:border-brand-300",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function ToggleGroup<T extends string>({
  options,
  selected,
  onChange,
}: {
  options: Array<{ value: T; label: string }>;
  selected: T[];
  onChange: (next: T[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => {
        const active = selected.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(active ? selected.filter((v) => v !== option.value) : [...selected, option.value])}
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs",
              active ? "border-brand-400 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300" : "border-border hover:border-brand-300",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-foreground">{label}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {children}
    </div>
  );
}

function NumberField({
  id,
  label,
  hint,
  value,
  onChange,
  max,
  suffix,
}: {
  id: string;
  label: string;
  hint?: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  max: number;
  suffix?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <div className="flex items-center gap-2">
        <Input
          id={id}
          inputMode="numeric"
          value={value ?? ""}
          placeholder="Any"
          onChange={(e) => {
            const raw = e.target.value.replace(/[^\d]/g, "");
            onChange(raw === "" ? undefined : Math.min(max, Number(raw)));
          }}
          className="max-w-[9rem]"
        />
        {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}

function MoreFiltersSheet({
  open,
  onOpenChange,
  catalog,
  filters,
  onChange,
  showCategories,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  catalog: TikTokCreatorSearchCatalog;
  filters: TikTokCreatorSearchFilters;
  onChange: (filters: TikTokCreatorSearchFilters) => void;
  showCategories: boolean;
  onApply: () => void;
}) {
  const set = (patch: Partial<TikTokCreatorSearchFilters>) => onChange({ ...filters, ...patch });
  const isUs = catalog.market === "US";
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b border-border px-6 py-4">
          <SheetTitle>More filters</SheetTitle>
          <SheetDescription>TikTok reports these over the last 30 days.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-7 overflow-y-auto px-6 py-5">
          <section className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Audience</h3>
            <Field label="Follower age">
              <ToggleGroup options={AGE_OPTIONS} selected={filters.ageRanges ?? []} onChange={(ageRanges) => set({ ageRanges })} />
            </Field>
            <Field label="Mostly">
              <ChoiceRow
                options={[
                  { value: "FEMALE" as const, label: "Women" },
                  { value: "MALE" as const, label: "Men" },
                ]}
                value={filters.gender}
                onChange={(gender) => set({ gender, genderMinPercent: gender ? filters.genderMinPercent : undefined })}
              />
              {filters.gender && (
                <div className="flex items-center gap-3 pt-1">
                  <input
                    id="creator-gender-share"
                    type="range"
                    min={50}
                    max={95}
                    step={5}
                    value={filters.genderMinPercent ?? 50}
                    onChange={(e) => set({ genderMinPercent: Number(e.target.value) === 50 ? undefined : Number(e.target.value) })}
                    className="flex-1 accent-brand-600"
                    aria-label="Minimum share of followers"
                  />
                  <span className="w-16 text-right text-sm tabular-nums text-muted-foreground">
                    {filters.genderMinPercent ? `${filters.genderMinPercent}%+` : "Any"}
                  </span>
                </div>
              )}
            </Field>
            {catalog.languages.length > 0 && (
              <Field label="Language">
                <ToggleGroup
                  options={catalog.languages.map((l) => ({ value: l.id, label: l.name }))}
                  selected={filters.languageIds ?? []}
                  onChange={(languageIds) => set({ languageIds })}
                />
              </Field>
            )}
          </section>

          <section className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Content</h3>
            <NumberField
              id="creator-min-views"
              label="Average video views, at least"
              value={filters.minAvgVideoViews}
              onChange={(minAvgVideoViews) => set({ minAvgVideoViews })}
              max={100_000}
            />
            <NumberField
              id="creator-min-engagement"
              label="Engagement rate, at least"
              hint="Whole percentage points, up to 20."
              value={filters.minEngagementRate}
              onChange={(v) => set({ minEngagementRate: v === undefined ? undefined : Math.max(1, v) })}
              max={20}
              suffix="%"
            />
            <NumberField
              id="creator-min-live"
              label="Average LIVE viewers, at least"
              value={filters.minLiveViewers}
              onChange={(minLiveViewers) => set({ minLiveViewers })}
              max={100_000}
            />
          </section>

          <section className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Affiliate</h3>
            {isUs && (
              <Field label="Units sold, lifetime">
                <ToggleGroup
                  options={UNITS_OPTIONS}
                  selected={filters.unitsSoldRanges ?? []}
                  onChange={(unitsSoldRanges) => set({ unitsSoldRanges })}
                />
              </Field>
            )}
            <Field label="Average commission under">
              <ChoiceRow
                options={[5, 10, 15, 20].map((n) => ({ value: n as 5 | 10 | 15 | 20, label: `${n}%` }))}
                value={filters.maxCommissionRate}
                onChange={(maxCommissionRate) => set({ maxCommissionRate })}
              />
            </Field>
            <Field label="Posts after receiving a sample" hint="TikTok’s rating of how reliably the creator posts.">
              <ChoiceRow
                options={[
                  { value: "OK" as const, label: "OK or better" },
                  { value: "GOOD" as const, label: "Good or better" },
                  { value: "BETTER" as const, label: "Better" },
                ]}
                value={filters.postRate}
                onChange={(postRate) => set({ postRate })}
              />
            </Field>
            <Field label="Management">
              <ChoiceRow
                options={[
                  { value: "INDEPENDENT" as const, label: "Independent" },
                  { value: "AGENCY_MANAGED" as const, label: "Agency managed" },
                ]}
                value={filters.agencyStatus}
                onChange={(agencyStatus) => set({ agencyStatus })}
              />
            </Field>
            <label className="flex items-center justify-between gap-3">
              <span className="text-sm text-foreground">
                Fast growing
                <span className="block text-xs text-muted-foreground">Top 10% growth in the last 30 days</span>
              </span>
              <Switch id="creator-fast-growing" checked={Boolean(filters.fastGrowing)} onCheckedChange={(fastGrowing) => set({ fastGrowing })} />
            </label>
            <label className="flex items-center justify-between gap-3">
              <span className="text-sm text-foreground">
                Not invited in the last 90 days
                <span className="block text-xs text-muted-foreground">Hides creators your shop already invited on TikTok</span>
              </span>
              <Switch
                id="creator-not-invited"
                checked={Boolean(filters.notInvitedLast90Days)}
                onCheckedChange={(notInvitedLast90Days) => set({ notInvitedLast90Days })}
              />
            </label>
          </section>

          {(catalog.creatorLevels.length > 0 || showCategories) && (
            <section className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Level and category</h3>
              {catalog.creatorLevels.length > 0 && (
                <Field label="Creator level" hint="TikTok’s GMV-based creator tier.">
                  <ToggleGroup
                    options={catalog.creatorLevels.map((l) => ({ value: l.id, label: l.name }))}
                    selected={filters.creatorLevelIds ?? []}
                    onChange={(creatorLevelIds) => set({ creatorLevelIds })}
                  />
                </Field>
              )}
              {showCategories && (
                <Field label="Product category">
                  <ToggleGroup
                    options={catalog.categories.map((c) => ({ value: c.id, label: c.name }))}
                    selected={filters.categoryIds ?? []}
                    onChange={(categoryIds) => set({ categoryIds })}
                  />
                </Field>
              )}
            </section>
          )}
        </div>

        <SheetFooter className="flex-row justify-start gap-2 border-t border-border px-6 py-4 sm:justify-start">
          <Button type="button" onClick={onApply}>
            <SearchIcon className="h-4 w-4" />
            Search
          </Button>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * TikTok-side ordering. Changing it re-runs the search, since TikTok sorts
 * the whole result set, not just the loaded page.
 */
export function SortSelect({
  catalog,
  value,
  onChange,
  disabled,
}: {
  catalog: TikTokCreatorSearchCatalog;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
}) {
  if (catalog.sorters.length === 0) return null;
  return (
    <Select value={value ?? ANY} onValueChange={(v) => onChange(v === ANY ? undefined : v)} disabled={disabled}>
      <SelectTrigger className="h-8 w-52 gap-1.5 text-xs" aria-label="Sort results">
        <span className="text-muted-foreground">Sort:</span>
        <span className="flex-1 truncate text-left">
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>TikTok’s ranking</SelectItem>
        {catalog.sorters
          .filter((s) => s.name !== "Most relevant")
          .map((s) => (
            <SelectItem key={s.id} value={s.id}>
              {s.name}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
}
